import { useDebouncedValue } from "@mantine/hooks";
import { useRef, useState } from "react";

import type { Address } from "../../../types";

import { getEligibility } from "../../../api/eligibility";
import { useEligibilityRequest } from "../../../hooks/useEligibilityRequest";
import { useSalesContext } from "../../../sales-context/SalesContext";
import {
    useIntegratedFlowStore,
    useProductOfferStore,
    useVerifiedCustomer,
    useVerifiedCustomerStore,
} from "../../../stores";
import { EligibilityStatus } from "../../../types";
import { useAddressSearch } from "./useAddressSearch";
import { DEFAULT_COUNTRY_CODE, useCollectCustomerInfoForm } from "./useCollectCustomerInfoForm";

export const useCollectCustomerInfo = () => {
    const { sessionId, logger: baseLogger, analytics, taskType, apiClients, features } = useSalesContext();
    const logger = baseLogger.child({ module: "useCollectCustomerInfo", supportTeam: "sales" });

    const customer = useVerifiedCustomer(sessionId);
    const { buildRequest } = useEligibilityRequest();
    const integratedStep = useIntegratedFlowStore((state) => state.getCurrentStep(sessionId));
    const selectedProduct = useProductOfferStore((state) => state.getSelectedProduct(sessionId));

    const { setCustomer } = useVerifiedCustomerStore.getState();
    const { updateIntegratedStep } = useIntegratedFlowStore.getState();

    if (!customer || !customer.mdn) {
        logger.warn("Customer data is undefined");
        throw new Error("Customer data is undefined");
    }

    if (!taskType.isVoice && !taskType.isChat) {
        logger.warn("Active task is not voice or chat task");
        throw new Error("Active task is not voice or chat task");
    }

    const form = useCollectCustomerInfoForm(customer);
    // The text of a picked suggestion (its label, then its full address), or the address on file. Searching it
    // again would bill another autocomplete call for an address the expert already has.
    const [selectedAddress, setSelectedAddress] = useState<string | undefined>(form.values.addressSearch);
    const [isResolvingAddress, setIsResolvingAddress] = useState(false);
    const [failedAddressSearch, setFailedAddressSearch] = useState<string>();
    const [debouncedAddressSearch] = useDebouncedValue(form.values.addressSearch, 300);
    // The input changes before the debounce catches up, so either value matching keeps a just-resolved pick from
    // searching its label one more time.
    const isAddressSelected =
        debouncedAddressSearch === selectedAddress || form.values.addressSearch === selectedAddress;
    const {
        suggestions: addressSuggestions,
        isLoading: isLoadingSuggestions,
        resolveAddress,
    } = useAddressSearch(debouncedAddressSearch, !isAddressSelected);
    const isLoading = isLoadingSuggestions || isResolvingAddress;
    const showAddressNotFoundMessage =
        failedAddressSearch === form.values.addressSearch ||
        (!isAddressSelected && debouncedAddressSearch.length > 6 && !isLoading && addressSuggestions.length === 0);

    // Only the latest pick's details call may update the form or clear the loading state.
    const latestPick = useRef(0);

    const handleAddressSelect = async (placeId: string) => {
        const suggestion = addressSuggestions.find((s) => s.placeId === placeId);
        if (!suggestion) return;
        const pick = ++latestPick.current;
        setSelectedAddress(suggestion.label);
        setFailedAddressSearch(undefined);
        setIsResolvingAddress(true);
        try {
            const address = await resolveAddress(placeId);
            // A later pick, or what the expert typed while the details call was out, wins.
            if (pick !== latestPick.current || form.getValues().addressSearch !== suggestion.label) return;
            setSelectedAddress(address);
            form.setFieldValue("addressSearch", address);
        } catch {
            if (pick !== latestPick.current) return;
            // fetchAddressDetails already logged it. Searching the label again lets the expert re-pick.
            setSelectedAddress(undefined);
            if (form.getValues().addressSearch !== suggestion.label) return;
            setFailedAddressSearch(suggestion.label);
            // Picking split the label into the address fields, but a label has no zip, so its parts don't line up
            // with them (the country lands in zip). Manual entry starts empty instead.
            form.setValues({ address1: "", city: "", state: "", zip: "", countryCode: DEFAULT_COUNTRY_CODE });
            form.setFieldError("addressSearch", "We couldn\u2019t get the full address for that suggestion.");
        } finally {
            if (pick === latestPick.current) setIsResolvingAddress(false);
        }
    };

    const [eligibilityStatus, setEligibilityStatus] = useState<EligibilityStatus | null>(null);
    const [isLoadingEligibility, setIsLoadingEligibility] = useState(false);
    const [sentIneligibleAddress, setSentIneligibleAddress] = useState(false);

    const handleContinueToPayment = async () => {
        try {
            setIsLoadingEligibility(true);

            const address: Address = {
                Address1: form.values.address1,
                City: form.values.city,
                StateProvinceCode: form.values.state,
                PostalCode: form.values.zip,
                CountryCode: form.values.countryCode,
            };

            const updatedCustomer = {
                ...customer,
                firstName: form.values.firstName,
                lastName: form.values.lastName,
                fullName: `${form.values.firstName} ${form.values.lastName}`,
                email: form.values.email,
                address,
            };
            setCustomer(sessionId, updatedCustomer);

            // The same request useEligibility sends, carrying the details just collected. A thinner
            // one would return quotes shaped differently from the check before it.
            const request = buildRequest(updatedCustomer);

            // getEligibility writes the full product list to the store and resolves once that list is the live one.
            // Read it from there. Don't overwrite it here: a primary-only write drops the tracking-only offers.
            await getEligibility(
                apiClients.sales,
                logger,
                analytics,
                request,
                features.isUsingMovedDiscoveryAndEngagementQuestions,
            );
            const selectedEligibleProduct = useProductOfferStore
                .getState()
                .getProducts(sessionId)
                .find((p) => p.productSku === selectedProduct?.productSku);

            if (!selectedEligibleProduct) {
                useProductOfferStore.getState().updateSelectedProduct(sessionId, undefined);
                updateIntegratedStep(sessionId, "SuggestedOfferStep");
            } else if (selectedEligibleProduct.status === EligibilityStatus.Eligible) {
                setEligibilityStatus(selectedEligibleProduct.status);
                useProductOfferStore.getState().updateSelectedProduct(sessionId, selectedEligibleProduct);
                updateIntegratedStep(sessionId, "SendLinkStep");
            } else {
                if (!sentIneligibleAddress) {
                    setSentIneligibleAddress(true);
                    void analytics.dispatcher.dispatchBusinessEvent("FirstIneligibleAddressReceived");
                } else {
                    setEligibilityStatus(selectedEligibleProduct.status);
                    useProductOfferStore.getState().updateSelectedProduct(sessionId, selectedEligibleProduct);
                    void analytics.dispatcher.dispatchBusinessEvent("SecondIneligibleAddressReceived");
                }
            }

            void analytics.dispatcher
                .withExtra({ nextStep: integratedStep })
                .dispatchBusinessEvent("ButtonClicked_ContinueToPayment");
        } catch (error) {
            logger.warn({ error }, "Error checking eligibility");
        } finally {
            setIsLoadingEligibility(false);
        }
    };

    return {
        addressSuggestions,
        customerMdn: customer.mdn,
        eligibilityStatus,
        form,
        handleAddressSelect,
        handleContinueToPayment,
        integratedStep,
        isLoading,
        isLoadingEligibility,
        product: selectedProduct,
        sentIneligibleAddress,
        setSentIneligibleAddress,
        showAddressNotFoundMessage,
    };
};
